import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calculator, Eraser, X, PencilLine, NotebookText } from 'lucide-react';

/**
 * Math workspace - floating draggable panel with scratch pad + calculator.
 * Used inside ChapterTest so students can work out calculations during a math test.
 */
const MathWorkspace = ({ defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  const [tab, setTab] = useState('pad'); // 'pad' | 'calc'
  const [notes, setNotes] = useState('');
  const [expr, setExpr] = useState('');
  const [result, setResult] = useState('');
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastPtRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    if (tab !== 'pad' || !open) return;
    const cv = canvasRef.current;
    if (!cv) return;
    cv.width = cv.offsetWidth;
    cv.height = cv.offsetHeight;
    const ctx = cv.getContext('2d');
    ctx.fillStyle = '#FFFEF7';
    ctx.fillRect(0, 0, cv.width, cv.height);
    // light grid for math feel
    ctx.strokeStyle = '#E5E7EB';
    ctx.lineWidth = 0.5;
    for (let x = 0; x < cv.width; x += 20) {
      ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, cv.height); ctx.stroke();
    }
    for (let y = 0; y < cv.height; y += 20) {
      ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(cv.width, y); ctx.stroke();
    }
  }, [tab, open]);

  const getPos = (e) => {
    const cv = canvasRef.current;
    const rect = cv.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    return { x, y };
  };

  const startDraw = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    lastPtRef.current = getPos(e);
  };

  const draw = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const p = getPos(e);
    ctx.strokeStyle = '#1E3A8A';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(lastPtRef.current.x, lastPtRef.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastPtRef.current = p;
  };

  const stopDraw = () => { drawingRef.current = false; };

  const clearCanvas = () => {
    const cv = canvasRef.current;
    if (!cv) return;
    const ctx = cv.getContext('2d');
    ctx.fillStyle = '#FFFEF7';
    ctx.fillRect(0, 0, cv.width, cv.height);
    ctx.strokeStyle = '#E5E7EB';
    ctx.lineWidth = 0.5;
    for (let x = 0; x < cv.width; x += 20) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, cv.height); ctx.stroke(); }
    for (let y = 0; y < cv.height; y += 20) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(cv.width, y); ctx.stroke(); }
  };

  // Calculator
  const calcBtn = (val) => {
    if (val === '=') {
      try {
        // Allow only digits, operators, parens, dot, %
        if (!/^[\d+\-*/().% ]+$/.test(expr)) {
          setResult('Error');
          return;
        }
        // eslint-disable-next-line no-new-func
        const r = Function(`"use strict"; return (${expr.replace(/×/g, '*').replace(/÷/g, '/')})`)();
        setResult(String(Math.round(r * 100000) / 100000));
      } catch {
        setResult('Error');
      }
    } else if (val === 'C') {
      setExpr(''); setResult('');
    } else if (val === '⌫') {
      setExpr(prev => prev.slice(0, -1));
    } else {
      setExpr(prev => prev + val);
    }
  };

  const calcKeys = [
    'C', '⌫', '(', ')',
    '7', '8', '9', '÷',
    '4', '5', '6', '×',
    '1', '2', '3', '-',
    '0', '.', '=', '+',
  ];

  return (
    <>
      {/* Floating toggle button */}
      <motion.button
        onClick={() => setOpen(!open)}
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.92 }}
        data-testid="workspace-toggle-btn"
        className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 text-white shadow-2xl flex items-center justify-center"
        title="Open Math Workspace">
        {open ? <X className="w-6 h-6" /> : <Calculator className="w-6 h-6" />}
      </motion.button>

      {/* Workspace panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, x: 50, scale: 0.95 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: 50, scale: 0.95 }}
            transition={{ type: 'spring', damping: 22 }}
            className="fixed bottom-24 right-6 z-40 w-[90vw] max-w-md bg-white rounded-3xl border-2 border-slate-200 shadow-2xl overflow-hidden"
            data-testid="math-workspace">
            
            <div className="bg-gradient-to-r from-indigo-500 to-purple-600 p-3 flex items-center justify-between">
              <div className="flex items-center gap-2 text-white font-bold">
                <Calculator className="w-5 h-5" />
                <span>Math Workspace</span>
              </div>
              <button onClick={() => setOpen(false)} className="text-white/80 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex bg-slate-50 border-b">
              <button onClick={() => setTab('pad')} data-testid="ws-tab-pad"
                className={`flex-1 py-2.5 text-sm font-bold flex items-center justify-center gap-1.5 transition ${tab === 'pad' ? 'bg-white text-indigo-600 border-b-2 border-indigo-500' : 'text-slate-500'}`}>
                <PencilLine className="w-4 h-4" />Scratch Pad
              </button>
              <button onClick={() => setTab('notes')} data-testid="ws-tab-notes"
                className={`flex-1 py-2.5 text-sm font-bold flex items-center justify-center gap-1.5 transition ${tab === 'notes' ? 'bg-white text-indigo-600 border-b-2 border-indigo-500' : 'text-slate-500'}`}>
                <NotebookText className="w-4 h-4" />Notes
              </button>
              <button onClick={() => setTab('calc')} data-testid="ws-tab-calc"
                className={`flex-1 py-2.5 text-sm font-bold flex items-center justify-center gap-1.5 transition ${tab === 'calc' ? 'bg-white text-indigo-600 border-b-2 border-indigo-500' : 'text-slate-500'}`}>
                <Calculator className="w-4 h-4" />Calculator
              </button>
            </div>

            {/* Content */}
            <div className="p-3">
              {tab === 'pad' && (
                <div>
                  <canvas
                    ref={canvasRef}
                    onMouseDown={startDraw} onMouseMove={draw} onMouseUp={stopDraw} onMouseLeave={stopDraw}
                    onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={stopDraw}
                    data-testid="ws-canvas"
                    className="w-full h-72 rounded-xl border-2 border-slate-200 bg-[#FFFEF7] cursor-crosshair touch-none" />
                  <button onClick={clearCanvas} data-testid="ws-clear-canvas"
                    className="mt-2 w-full py-2 bg-amber-100 text-amber-700 rounded-xl font-bold text-sm flex items-center justify-center gap-1.5 hover:bg-amber-200">
                    <Eraser className="w-4 h-4" />Clear Pad
                  </button>
                </div>
              )}

              {tab === 'notes' && (
                <div>
                  <textarea
                    value={notes} onChange={(e) => setNotes(e.target.value)}
                    placeholder="Type your working here... e.g. 5x + 3 = 18&#10;5x = 15&#10;x = 3"
                    data-testid="ws-notes-area"
                    className="w-full h-72 p-3 bg-yellow-50 border-2 border-yellow-200 rounded-xl text-sm font-mono leading-relaxed focus:border-indigo-500 outline-none resize-none" />
                  <p className="text-xs text-slate-500 mt-1.5 text-center">Notes are saved while the test is open</p>
                </div>
              )}

              {tab === 'calc' && (
                <div>
                  <div className="bg-slate-900 rounded-xl p-3 mb-2 text-right">
                    <div className="text-slate-400 text-sm h-5 truncate" data-testid="ws-calc-expr">{expr || '0'}</div>
                    <div className="text-white text-2xl font-bold h-9 truncate" data-testid="ws-calc-result">{result}</div>
                  </div>
                  <div className="grid grid-cols-4 gap-1.5">
                    {calcKeys.map(k => {
                      const isOp = ['÷', '×', '-', '+', '='].includes(k);
                      const isAction = ['C', '⌫'].includes(k);
                      return (
                        <button key={k} onClick={() => calcBtn(k)} data-testid={`ws-calc-key-${k}`}
                          className={`py-3 rounded-xl font-bold text-base transition ${
                            k === '=' ? 'bg-indigo-500 text-white col-span-1' :
                            isOp ? 'bg-indigo-100 text-indigo-700' :
                            isAction ? 'bg-red-100 text-red-700' :
                            'bg-slate-100 text-slate-800 hover:bg-slate-200'
                          }`}>{k}</button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default MathWorkspace;
